import { DateTimePeriodParams } from '@brenger/api-client';
import cn from 'classnames';
import * as React from 'react';
import { IconEcoFriendly, InputTileOption } from '../../../brenger-shared-ui';
import { SimplePrice } from '../../../brenger-shared-ui/components/other/pricedisplay/SimplePrice';
import { useTypedSelector } from '../../../hooks';
import { formatDate } from '../../../utils/datetime';
import { translate } from '../../../utils/localization';
import { getPickupSituation, getStopSelectedTime, getStopSituation, getStopTimeOptions } from '../ducks';
import { GENERAL_DEFAULT_TIME_START_STORE } from '../../../utils/global';
import { getHours } from 'date-fns';

const priceLabelColor: Record<string, string> = {
  red: 'text-[#e10000]',
  orange: 'text-[#f59023]',
  green: 'text-secondary',
  yellow: 'text-[#ffc63c]',
};

export type TimeOption = InputTileOption<DateTimePeriodParams>;

interface UseTime {
  loading: boolean;
  options: { full: TimeOption[]; custom: TimeOption[] };
}

export const useTimeOptions = (stopType: 'pickup' | 'delivery'): UseTime => {
  const loading = useTypedSelector(state => state.generalTransport.price.isLoading);
  const situation = useTypedSelector(state => getStopSituation(state, stopType));

  /**
   * FULL time slots
   */
  const optionsFull = useTypedSelector(state => getStopTimeOptions(state, stopType, 'full'));
  const full: TimeOption[] = optionsFull
    /**
     * Simple sort on price change, so that the cheapest option is the first one
     */
    .sort((a, b) => (a.price_change?.incl_vat.amount || 0) - (b.price_change?.incl_vat.amount || 0))
    .map(option => {
      const isMostBenificial = optionsFull.length === 1 || (option.price_change?.incl_vat.amount ?? 0) < 0;
      return {
        value: {
          start: option.start as string,
          end: option.end as string,
        },
        title: String(
          translate('request_flow.time.first_option.label_' + situation, {
            start: formatDate(option.start || '', 'hour-minute'),
            end: formatDate(option.end || '', 'hour-minute'),
          })
        ),
        suffix: (
          <span
            className={cn('flex items-center', {
              'text-[#ffc63c]': !isMostBenificial,
              'text-secondary': isMostBenificial,
            })}
          >
            {isMostBenificial && <IconEcoFriendly />}
            <SimplePrice price={option.price_change} addPlusMinusIndicator={true} />
          </span>
        ),
      };
    });

  /**
   * CUSTOM time slots
   */
  const optionsCustom = useTypedSelector(state => getStopTimeOptions(state, stopType, 'custom'));
  /**
   * FIXME:
   * We concluded that store pickup time should also be adjusted when an early delivery is selected.
   * Apparently pricing generates timeslots that are earlier then 09:00 (default store start time), when pickup full time slot is selectede
   * This is unfortunate:
   * Because selecting 08:00 as delivery time, would update the pickup to 08:00 as well, and this not what we want.
   * So we filter out the custom time options that are earlier then 09:00
   * BUT only when pickup full time and store is selected
   * Created issue: https://gitlab.com/brenger/api/pymono/-/issues/927
   */
  const isStore = useTypedSelector(getPickupSituation) === 'store';
  const pickupTime = useTypedSelector(state => getStopSelectedTime(state, 'pickup'));
  const pickupStartHour = pickupTime ? getHours(new Date(pickupTime.start)) : undefined;
  const isShouldFilterCustomTimeOptions =
    stopType === 'delivery' && isStore && pickupStartHour === GENERAL_DEFAULT_TIME_START_STORE;
  const filteredCustomTimeOptions = isShouldFilterCustomTimeOptions
    ? optionsCustom.filter(option => {
        const startHour = getHours(new Date(option.start || ''));
        return startHour >= GENERAL_DEFAULT_TIME_START_STORE;
      })
    : optionsCustom;
  /** end FIXME */
  const custom: TimeOption[] = filteredCustomTimeOptions.map(option => {
    let label = 'green';
    if (option.price_change?.incl_vat.amount) {
      label = option.price_change.incl_vat.amount > 1250 ? 'red' : 'orange';
    }
    return {
      value: { start: option.start as string, end: option.end as string },
      title: `${formatDate(option.start || '', 'hour-minute')} - ${formatDate(option.end || '', 'hour-minute')}`,
      suffix: (
        <span className={priceLabelColor[label]}>
          {option.price_change?.incl_vat.amount === 0 && <IconEcoFriendly />}
          <SimplePrice price={option.price_change} addPlusMinusIndicator={true} />
        </span>
      ),
    };
  });

  return {
    loading,
    options: { full, custom },
  };
};
